import { useState } from 'react'

import { FxAlert, FxButton } from '@/components/fx'
import { SettingsDialog } from './SettingsDialog'
import { apiKeyErrorMessage, type ApiKey } from './apiKeys'
import { useRevokeApiKey } from './useApiKeys'

/**
 * Widerrufen is immediate and cannot be undone: the gateway keeps only the
 * key's hash, so a revoked key cannot be restored and anything still sending
 * it stops working on the next request.
 *
 * The confirmation therefore names the key and says what breaks, rather than
 * asking "Sind Sie sicher?" — a question nobody can answer without knowing
 * which of three keys is about to stop working.
 */
export function RevokeApiKeyDialog({
  apiKey,
  onClose,
}: {
  /** The key to revoke; `null` keeps the dialog closed. */
  apiKey: ApiKey | null
  onClose: () => void
}) {
  const [error, setError] = useState<string | null>(null)
  const { mutate, isPending } = useRevokeApiKey()

  function close() {
    if (isPending) return
    setError(null)
    onClose()
  }

  function revoke() {
    if (!apiKey) return
    setError(null)
    mutate(apiKey.id, {
      onSuccess: () => {
        setError(null)
        onClose()
      },
      onError: (cause) =>
        setError(apiKeyErrorMessage(cause, 'Der Schlüssel konnte nicht widerrufen werden.')),
    })
  }

  return (
    <SettingsDialog
      open={apiKey !== null}
      onOpenChange={(next) => {
        if (!next) close()
      }}
      title="Schlüssel widerrufen"
      description={
        apiKey
          ? `„${apiKey.name}“ wird sofort ungültig. Jede Anbindung, die ihn benutzt, verliert den Zugang beim nächsten Aufruf. Rückgängig machen können wir das nicht — es bleibt nur, einen neuen Schlüssel anzulegen und ihn dort einzutragen.`
          : undefined
      }
      footer={
        <>
          <FxButton variant="outlined" onClick={close} disabled={isPending}>
            Abbrechen
          </FxButton>
          <FxButton variant="danger" onClick={revoke} disabled={isPending}>
            {isPending ? 'Wird widerrufen …' : 'Endgültig widerrufen'}
          </FxButton>
        </>
      }
    >
      {error && (
        <FxAlert tone="danger">
          <p>{error}</p>
        </FxAlert>
      )}
    </SettingsDialog>
  )
}
